/**
 * Feature flags for controlling optional functionality.
 *
 * Resolution order (highest priority first):
 *   1. Environment variable override (VITE_FEATURE_*, VITE_FF_*, VITE_UI_*)
 *   2. Runtime DB value (populated by FeatureFlagProvider via hydrateFromRuntime)
 *   3. Hardcoded default in this file
 *
 * The env var always wins so developers can force a value locally without
 * needing a DB row. In production the DB is the primary control surface.
 */

// ---------------------------------------------------------------------------
// Env helpers (unchanged)
// ---------------------------------------------------------------------------

const envFlag = (key: string): boolean | undefined => {
  if (typeof import.meta !== "undefined" && import.meta.env && key in import.meta.env) {
    const raw = String(import.meta.env[key]).toLowerCase();
    return raw === "true" || raw === "1";
  }
  return undefined;
};

/** ui_uplift from the build env alone, ignoring runtime rows (used while another org's rows are loaded). */
export function uiUpliftFromEnv(): "off" | "preview" | "enabled" {
  return envEnum("VITE_UI_UPLIFT", ["off", "preview", "enabled"] as const) ?? "off";
}

const envEnum = <T extends string>(key: string, allowed: readonly T[]): T | undefined => {
  if (typeof import.meta !== "undefined" && import.meta.env && key in import.meta.env) {
    const raw = String(import.meta.env[key]).toLowerCase() as T;
    if (allowed.includes(raw)) return raw;

    const allowedStrings = allowed as readonly string[];
    if ((raw === "false" || raw === "0") && allowedStrings.includes("off")) return "off" as T;
    if ((raw === "true" || raw === "1") && allowedStrings.includes("enabled")) return "enabled" as T;
  }
  return undefined;
};

// ---------------------------------------------------------------------------
// Runtime store — populated by FeatureFlagProvider
// ---------------------------------------------------------------------------

/**
 * Mutable runtime store. Keys are DB `flag_key` values.
 * Written to by `hydrateFromRuntime()`, read by the flag getters below.
 */
const _runtime: Record<string, { enabled: boolean; value: string | null }> = {};

/**
 * Called by FeatureFlagProvider whenever the resolved flag map changes.
 * This bridges the React context world into the static module so that
 * non-React code (e.g. service-instrumentation.ts) can read flags.
 */
export function hydrateFromRuntime(
  flags: Record<string, { enabled: boolean; value: string | null }>
) {
  // Clear stale keys
  for (const k of Object.keys(_runtime)) {
    if (!(k in flags)) delete _runtime[k];
  }
  // Copy new values
  for (const [k, v] of Object.entries(flags)) {
    _runtime[k] = v;
  }
}

/** Read a boolean flag: env override → runtime DB → hardcoded default */
function resolveBoolean(envKey: string, dbKey: string, hardDefault: boolean): boolean {
  const env = envFlag(envKey);
  if (env !== undefined) return env;
  if (dbKey in _runtime) return _runtime[dbKey].enabled;
  return hardDefault;
}

/** Read an enum flag: env override → runtime DB value → hardcoded default */
function resolveEnum<T extends string>(
  envKey: string,
  dbKey: string,
  hardDefault: T,
  allowed: readonly T[]
): T {
  const env = envEnum(envKey, allowed);
  if (env !== undefined) return env;
  if (dbKey in _runtime) {
    const dbVal = _runtime[dbKey].value as T | null;
    if (dbVal && (allowed as readonly string[]).includes(dbVal)) return dbVal;
    // If only enabled/disabled matters, map boolean
    if (_runtime[dbKey].enabled && (allowed as readonly string[]).includes("enabled")) return "enabled" as T;
    if (!_runtime[dbKey].enabled && (allowed as readonly string[]).includes("off")) return "off" as T;
  }
  return hardDefault;
}

// ---------------------------------------------------------------------------
// Public FEATURE_FLAGS object
//
// Uses getters so every read goes through the resolution chain.
// Consumers still import and use `FEATURE_FLAGS.mfa` exactly as before.
// ---------------------------------------------------------------------------

export const FEATURE_FLAGS = {
  get mfa() {
    return resolveBoolean("VITE_FEATURE_MFA", "mfa", true);
  },
  get segmentBuilder() {
    return resolveBoolean("VITE_FEATURE_SEGMENT_BUILDER", "segment_builder", true);
  },
  get multiChannelSendRollout(): "off" | "pilot" | "enabled" {
    return resolveEnum(
      "VITE_FEATURE_MULTI_CHANNEL_SEND",
      "multi_channel_send",
      "enabled",
      ENUM_FLAG_VALUES.multi_channel_send
    );
  },
  get multiChannelSend() {
    return this.multiChannelSendRollout !== "off";
  },
  get payrollIntegration() {
    return resolveBoolean("VITE_FEATURE_PAYROLL_INTEGRATION", "payroll_integration", true);
  },
  /** Kill-switch flags for service reliability rollout (§4.1 of reliability spec) */
  get runOfServiceReliability() {
    return resolveBoolean("VITE_FF_RUN_OF_SERVICE_RELIABILITY", "run_of_service_reliability", false);
  },
  get planningViewReliability() {
    return resolveBoolean("VITE_FF_PLANNING_VIEW_RELIABILITY", "planning_view_reliability", false);
  },
  get matrixViewReliability() {
    return resolveBoolean("VITE_FF_MATRIX_VIEW_RELIABILITY", "matrix_view_reliability", false);
  },
  /**
   * Auto-scheduler kill-switch. The auto-scheduler reads/writes 4 tables
   * (`volunteer_qualifications`, `scheduling_proposals`, `scheduling_assignments`,
   * `assignment_overrides`) covered by the committed migration chain. Keep the
   * flag default off until production schema history and an end-to-end UAT run
   * confirm the rollout — see audit:
   * https://www.notion.so/35421d35b8618199bc18fbfe07186ca2
   */
  get autoScheduler() {
    return resolveBoolean("VITE_FF_AUTO_SCHEDULER", "auto_scheduler", false);
  },
  /**
   * Today view: replace the role-templated widget dashboard with a single
   * ranked Attention Queue + ambient widgets demoted to a secondary tab.
   * Default off; flip on per-org once Phase 1 is verified. Spec:
   * docs/superpowers/specs/forge-today-view-attention-driven.md
   */
  get todayAttentionQueueV1() {
    return resolveBoolean(
      "VITE_FF_TODAY_ATTENTION_QUEUE_V1",
      "today_attention_queue_v1",
      false,
    );
  },
  /**
   * UI uplift rollout (docs/design/README.md). `off` renders the classic chassis,
   * `enabled` the new one, and `preview` the classic one unless the viewer opts in
   * with `?ui=next` (see src/lib/ui-uplift.ts). The ui-uplift preview build sets
   * VITE_UI_UPLIFT=enabled.
   */
  get uiUplift(): "off" | "preview" | "enabled" {
    const allowed = ENUM_FLAG_VALUES.ui_uplift;
    // A disabled DB row (or disabled org override, which keeps the copied value_text)
    // means off, whatever rollout value it still stores. The env override still wins.
    if (envEnum("VITE_UI_UPLIFT", allowed) === undefined && _runtime.ui_uplift?.enabled === false) {
      return "off";
    }
    return resolveEnum("VITE_UI_UPLIFT", "ui_uplift", "off", allowed);
  },
  ui: {
    get moduleIdentity(): "off" | "subtle" {
      return resolveEnum(
        "VITE_UI_MODULE_IDENTITY",
        "ui_module_identity",
        "off",
        ENUM_FLAG_VALUES.ui_module_identity
      );
    },
  },
};

// ---------------------------------------------------------------------------
// DB key ↔ display name mapping (used by admin UI)
// ---------------------------------------------------------------------------

/** Allowed values of each enum flag, keyed by DB flag_key; the admin editor offers these. */
export const ENUM_FLAG_VALUES = {
  multi_channel_send: ["off", "pilot", "enabled"],
  ui_uplift: ["off", "preview", "enabled"],
  ui_module_identity: ["off", "subtle"],
} as const satisfies Record<string, readonly string[]>;

export const FLAG_REGISTRY: Record<string, { label: string; envKey: string }> = {
  mfa:                          { label: "Multi-Factor Authentication",          envKey: "VITE_FEATURE_MFA" },
  segment_builder:              { label: "Segment Builder",                      envKey: "VITE_FEATURE_SEGMENT_BUILDER" },
  multi_channel_send:           { label: "Multi-Channel Send",                   envKey: "VITE_FEATURE_MULTI_CHANNEL_SEND" },
  payroll_integration:          { label: "Payroll Integration",                  envKey: "VITE_FEATURE_PAYROLL_INTEGRATION" },
  run_of_service_reliability:   { label: "Run-of-Service Reliability",           envKey: "VITE_FF_RUN_OF_SERVICE_RELIABILITY" },
  planning_view_reliability:    { label: "Planning View Reliability",            envKey: "VITE_FF_PLANNING_VIEW_RELIABILITY" },
  matrix_view_reliability:      { label: "Matrix View Reliability",              envKey: "VITE_FF_MATRIX_VIEW_RELIABILITY" },
  auto_scheduler:               { label: "Auto-Scheduler",                       envKey: "VITE_FF_AUTO_SCHEDULER" },
  today_attention_queue_v1:     { label: "Today Attention Queue (v1)",           envKey: "VITE_FF_TODAY_ATTENTION_QUEUE_V1" },
  ui_module_identity:           { label: "UI Module Identity",                   envKey: "VITE_UI_MODULE_IDENTITY" },
  ui_uplift:                    { label: "UI Uplift",                            envKey: "VITE_UI_UPLIFT" },
};
